import { UIEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { seconds } from '@utils/milliseconds';
import { transitionSlowest } from '@utils/transitions';

import { useAIBuilderChatSession, useFixFindingWithNavigator } from 'api/ai-builder/hooks';
import {
  useIgnoreFindings,
  useInvalidateJourneyReviews,
  useCancelJourneyReview,
  useLatestJourneyReview,
  useRestoreFindings,
  useStartJourneyReview,
} from 'api/journey-reviews/hooks';
import { ReviewFinding, ReviewSeverity } from 'api/journey-reviews/requests';
import { findingSignature, signatureKey } from 'api/journey-reviews/signature';
import { usePermissions } from 'contexts/permissions-context';
import { useFeature } from 'ui/hooks';

import { resolveReviewPanelState } from './resolve-panel-state';
import { ReviewPanelView } from './review-panel-view';
import { hasLostAny, resolveNavigatorStatus } from './review-utils';
import { useFindingNavigation } from './use-finding-navigation';
import { useJourneyReviewWebSocket } from './use-journey-review-websocket';
import { useLiveTargetIds } from './use-live-target-ids';
import { isGroupOpen, useReviewGroupsStore } from './use-review-groups-store';
import { useReviewPanelStore } from './use-review-panel-store';

import {
  setAiBuilderPanelOpen,
  useAiBuilderPanelStore,
} from '../ai-builder/use-ai-builder-panel-store';

export type ReviewPanelProps = {
  journeyShortId: string;
};

const ATTRIBUTES_SECTION = 'attributes';
const NAVIGATOR_SESSION_POLL_INTERVAL = seconds(3);

// No timeout of its own: the task's own time limit is what turns a stuck run into
// a `failed` row, so the panel always shows what actually happened.

// Module-level so the offset survives the panel unmounting on a tab change.
const scrollTopByJourney = new Map<string, number>();

export const ReviewPanel = ({ journeyShortId }: ReviewPanelProps) => {
  const { isOpen, close, setRunWebsocketId, clearRunWebsocketId, setFixRequest, clearFixRequest } =
    useReviewPanelStore();
  const websocketId = useReviewPanelStore(
    (store) => store.runWebsocketIdByJourney[journeyShortId] ?? null,
  );
  const sentFindingKey = useReviewPanelStore(
    (store) => store.fixRequestByJourney[journeyShortId] ?? null,
  );
  const { isEnabled: hasAiBuilderFeature } = useFeature('FEATURE_AI_BUILDER');
  const { canFixWithNavigator: hasNavigatorFixPermission } = usePermissions();
  const canFixWithNavigator = hasAiBuilderFeature && hasNavigatorFixPermission;
  const isNavigatorPanelOpen = useAiBuilderPanelStore((state) => state.value);
  const navigate = useNavigate();
  const location = useLocation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const isStartingRef = useRef(false);
  // The review has landed but the loading checklist is still playing out.
  const [isSettling, setIsSettling] = useState(false);
  const [wasRunning, setWasRunning] = useState(false);

  const { data: review, isInitialLoading } = useLatestJourneyReview(journeyShortId, {
    enabled: isOpen,
  });

  // A run in flight IS an in_progress review, so this survives the panel being
  // closed, unmounted, or started elsewhere.
  const isRunning = review?.status === 'in_progress';
  const startReview = useStartJourneyReview(journeyShortId);
  const cancelReview = useCancelJourneyReview(journeyShortId);
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();
  const ignoreFindings = useIgnoreFindings(journeyShortId);
  const restoreFindings = useRestoreFindings(journeyShortId);
  const jumpToFinding = useFindingNavigation();
  const openBySeverity = useReviewGroupsStore((store) => store.openByJourney[journeyShortId]);
  const setGroupOpen = useReviewGroupsStore((store) => store.setGroupOpen);

  const isSendingRef = useRef(false);
  const fixFinding = useFixFindingWithNavigator(journeyShortId, {
    onSuccess: () => setAiBuilderPanelOpen(true),
    onError: (_error, signature) => {
      const sentKey = useReviewPanelStore.getState().fixRequestByJourney[journeyShortId];
      if (sentKey === signatureKey(signature)) clearFixRequest(journeyShortId);
    },
    onSettled: () => {
      isSendingRef.current = false;
    },
  });
  const isAwaitingNavigatorIdle = Boolean(sentFindingKey) && !fixFinding.isLoading;
  const sessionQuery = useAIBuilderChatSession(journeyShortId, {
    enabled: isOpen && canFixWithNavigator,
    // An open Navigator panel keeps this cache fresh over its websocket; with the
    // panel closed the poll is the only thing that releases the busy lock. It waits
    // for the send to land so a poll that predates the POST cannot report idle.
    refetchInterval: (session) =>
      !isNavigatorPanelOpen && (isAwaitingNavigatorIdle || session?.isGenerating)
        ? NAVIGATOR_SESSION_POLL_INTERVAL
        : false,
  });
  const navigatorSession = sessionQuery.data;
  const navigatorStatus = resolveNavigatorStatus({
    isSending: fixFinding.isLoading,
    session: navigatorSession,
    isSessionLoading: sessionQuery.isInitialLoading,
  });

  // Between the click and the mutation's onSuccess the cached session still reads
  // idle, so without the isLoading guard the sent mark would clear on the next render.
  useEffect(() => {
    if (
      !sentFindingKey ||
      fixFinding.isLoading ||
      !navigatorSession ||
      navigatorSession.isGenerating
    ) {
      return;
    }
    clearFixRequest(journeyShortId);
    invalidateLatestJourneyReview(journeyShortId);
  }, [
    sentFindingKey,
    fixFinding.isLoading,
    navigatorSession,
    clearFixRequest,
    invalidateLatestJourneyReview,
    journeyShortId,
  ]);

  // A fix can land on a run nobody sent from here, such as the Build after a plan.
  const isNavigatorGenerating = Boolean(navigatorSession?.isGenerating);
  const wasNavigatorGeneratingRef = useRef(isNavigatorGenerating);
  useEffect(() => {
    const hasNavigatorFinished = wasNavigatorGeneratingRef.current && !isNavigatorGenerating;
    wasNavigatorGeneratingRef.current = isNavigatorGenerating;
    if (hasNavigatorFinished) invalidateLatestJourneyReview(journeyShortId);
  }, [isNavigatorGenerating, invalidateLatestJourneyReview, journeyShortId]);

  const liveTargetIds = useLiveTargetIds(journeyShortId);
  const previousTargetIdsRef = useRef(liveTargetIds);
  useEffect(() => {
    const hasDeletedTarget = hasLostAny(previousTargetIdsRef.current, liveTargetIds);
    previousTargetIdsRef.current = liveTargetIds;
    if (hasDeletedTarget) invalidateLatestJourneyReview(journeyShortId);
  }, [liveTargetIds, invalidateLatestJourneyReview, journeyShortId]);

  const handleFixWithNavigator = useCallback(
    (finding: ReviewFinding) => {
      if (isSendingRef.current) return;
      isSendingRef.current = true;

      const signature = findingSignature(finding);
      setFixRequest(journeyShortId, signatureKey(signature));
      fixFinding.mutate(signature);
    },
    [fixFinding, journeyShortId, setFixRequest],
  );

  // A list either way, so ignoring a whole severity group is one request and one
  // cache invalidation rather than one of each per finding.
  const handleIgnore = useCallback(
    (findings: ReviewFinding[]) => ignoreFindings.mutate(findings.map(findingSignature)),
    [ignoreFindings],
  );
  const handleRestore = useCallback(
    (findings: ReviewFinding[]) => restoreFindings.mutate(findings.map(findingSignature)),
    [restoreFindings],
  );

  // The socket only pulls the result forward; the poll guarantees it arrives.
  const handleComplete = useCallback(() => {
    invalidateLatestJourneyReview(journeyShortId);
  }, [invalidateLatestJourneyReview, journeyShortId]);

  useJourneyReviewWebSocket({
    websocketId,
    isEnabled: Boolean(isRunning && websocketId),
    onComplete: handleComplete,
  });

  // Play the checklist out only if this panel watched the run; reopening after
  // it finished goes straight to the result.
  if (isRunning && !wasRunning) {
    setWasRunning(true);
  } else if (!isRunning && wasRunning) {
    setWasRunning(false);
    setIsSettling(true);
  }

  useEffect(() => {
    if (isSettling) clearRunWebsocketId(journeyShortId);
  }, [isSettling, journeyShortId, clearRunWebsocketId]);

  const handleLoadingFinished = useCallback(() => setIsSettling(false), []);

  const handleStopReview = useCallback(() => {
    // Cancelling deletes the run, so forget we saw it running — otherwise the
    // checklist plays out over a review the user just stopped.
    cancelReview.mutate(undefined, {
      onSuccess: () => {
        setWasRunning(false);
        setIsSettling(false);
        clearRunWebsocketId(journeyShortId);
      },
    });
  }, [cancelReview, clearRunWebsocketId, journeyShortId]);

  const handleRunReview = useCallback(() => {
    // Two clicks in one tick see the same state, so only a ref settles it; a
    // genuine concurrent run is rejected server-side with a 409.
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    startReview.mutate(undefined, {
      onSuccess: (started) => setRunWebsocketId(journeyShortId, started.websocketId),
      onSettled: () => {
        isStartingRef.current = false;
      },
    });
  }, [startReview, setRunWebsocketId, journeyShortId]);

  const handleJumpTo = useCallback(
    (finding: ReviewFinding) => {
      if (finding.target.kind === 'field') {
        const segments = location.pathname.replace(/\/$/, '').split('/');
        segments[segments.length - 1] = ATTRIBUTES_SECTION;
        const attributesPath = segments.join('/');
        navigate(
          finding.target.id ? `${attributesPath}?highlight=${finding.target.id}` : attributesPath,
        );
        return;
      }
      jumpToFinding(finding.target);
    },
    [jumpToFinding, navigate, location.pathname],
  );

  const handleToggleGroup = useCallback(
    (severity: ReviewSeverity) =>
      setGroupOpen({
        journeyShortId,
        severity,
        isOpen: !isGroupOpen(openBySeverity, severity),
      }),
    [setGroupOpen, journeyShortId, openBySeverity],
  );

  const handleBodyScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      scrollTopByJourney.set(journeyShortId, event.currentTarget.scrollTop);
    },
    [journeyShortId],
  );

  const state = resolveReviewPanelState({
    isRunning: Boolean(isRunning),
    isSettling,
    review: review ?? null,
    isInitialLoading,
  });

  // On results appearing, not on every content change, so ignoring a finding
  // doesn't jump you back to the top.
  useLayoutEffect(() => {
    if (isOpen && state === 'results' && bodyRef.current) {
      bodyRef.current.scrollTop = scrollTopByJourney.get(journeyShortId) ?? 0;
    }
  }, [isOpen, state, journeyShortId]);

  return (
    <Panel $isOpen={isOpen} aria-hidden={!isOpen}>
      {isOpen && (
        <ReviewPanelView
          state={state}
          review={review ?? null}
          isFinishing={!isRunning && isSettling}
          onLoadingFinished={handleLoadingFinished}
          onStopReview={handleStopReview}
          isStoppingReview={cancelReview.isLoading}
          onRunReview={handleRunReview}
          onClose={close}
          onJumpTo={handleJumpTo}
          onIgnore={handleIgnore}
          onRestore={handleRestore}
          openBySeverity={openBySeverity}
          onToggleGroup={handleToggleGroup}
          bodyRef={bodyRef}
          onBodyScroll={handleBodyScroll}
          onFixWithNavigator={canFixWithNavigator ? handleFixWithNavigator : undefined}
          sentFindingKey={sentFindingKey}
          navigatorStatus={navigatorStatus}
        />
      )}
    </Panel>
  );
};

const Panel = styled.div<{ $isOpen: boolean }>((props) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: pxToRem(440),
  display: 'flex',
  flexDirection: 'column',
  background: theme.color.backgroundLightDark,
  borderLeft: `1px solid ${theme.color.border10}`,
  boxShadow: theme.shadow.fancyRight,
  transform: props.$isOpen ? 'translateX(0)' : 'translateX(100%)',
  transition: transitionSlowest('transform'),
  zIndex: 5,
}));
